import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { AuthService } from '../../../../core/services/auth.service';
import { TPipe } from '../../../../shared/pipes';
import { AppModalComponent } from '../../../../shared/ui/modal/app-modal.component';
import { OnboardingStore } from '../../stores/onboarding.store';

@Component({
  selector: 'app-onboarding-step-welcome',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TPipe, AppModalComponent],
  template: `
    <div class="flex flex-col items-center text-center px-2 py-8">
      <div
        class="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]"
      >
        <lucide-icon name="rocket" [size]="36" aria-hidden="true"></lucide-icon>
      </div>
      <h2 class="text-2xl font-semibold text-[var(--text)]">
        {{ 'onboarding.welcome.title' | t: { firstName: firstName() } }}
      </h2>
      <p class="mt-3 max-w-md text-base text-[var(--muted)]">
        {{ 'onboarding.welcome.subtitle' | t }}
      </p>
      <div class="mt-8 flex flex-col items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-xl bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
          [disabled]="busy()"
          (click)="start()"
        >
          {{ 'onboarding.welcome.cta' | t }}
        </button>
        <button
          type="button"
          class="text-sm font-medium text-[var(--muted)] hover:text-[var(--text)] underline-offset-2 hover:underline"
          [disabled]="busy()"
          (click)="askSkip()"
        >
          {{ 'onboarding.welcome.skip' | t }}
        </button>
      </div>
    </div>

    <app-modal
      [visible]="confirmOpen()"
      [stackAbove]="true"
      [closeOnOverlay]="false"
      [closeOnEscape]="true"
      [showCloseButton]="false"
      [size]="'sm'"
      [shrinkWrapBody]="true"
      [showFooter]="false"
      (closed)="cancelSkip()"
    >
      <div modalBody class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-semibold text-[var(--text)]">
            {{ 'onboarding.welcome.skipconfirm.title' | t }}
          </h3>
          <p class="text-sm text-[var(--muted)]">
            {{ 'onboarding.welcome.skipconfirm.subtitle' | t }}
          </p>
        </div>
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-[var(--border)] px-4 py-2 text-sm text-[var(--text)] hover:bg-[var(--hover)]"
            [disabled]="busy()"
            (click)="cancelSkip()"
          >
            {{ 'onboarding.welcome.skipconfirm.cancel' | t }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            [disabled]="busy()"
            (click)="confirmSkip()"
          >
            {{ 'onboarding.welcome.skipconfirm.confirm' | t }}
          </button>
        </div>
      </div>
    </app-modal>
  `,
})
export class OnboardingStepWelcomeComponent {
  private readonly auth = inject(AuthService);
  private readonly store = inject(OnboardingStore);

  protected readonly firstName = computed(() => {
    const name = this.auth.currentUser()?.name ?? '';
    return name.split(' ')[0] ?? '';
  });
  protected readonly busy = signal(false);
  protected readonly confirmOpen = signal(false);

  async start(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      await this.store.advance(1);
    } finally {
      this.busy.set(false);
    }
  }

  askSkip(): void {
    this.confirmOpen.set(true);
  }

  cancelSkip(): void {
    this.confirmOpen.set(false);
  }

  async confirmSkip(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      await this.store.skip();
      this.confirmOpen.set(false);
    } finally {
      this.busy.set(false);
    }
  }
}
